import * as THREE from 'three';

export class ThirdPersonCamera {
    constructor(camera, target, options = {}) {
        this.camera = camera;
        this.target = target;
        this.options = {
            distance: 7.0,                    // Camera distance from player
            height: 2.0,                      // Height offset above player
            smoothing: 0.1,
            verticalSmoothingFactor: 0.05,    // Slower vertical smoothing
            minDistance: 5,
            maxDistance: 15,
            minPitch: -Math.PI * 0.65,        // Match the max pitch for symmetry
            maxPitch: Math.PI * 0.65,         // Allow more upward rotation (about 117°)
            sensitivity: 0.002,
            horizontalOffset: 2.0,            // Right side offset
            lookAheadDistance: 5.0,           // Distance ahead of player to focus camera
            terrainBuffer: 2.0,               // Base distance to keep from terrain
            aimZoomMultiplier: 0.45,          // Zoom factor (0.45 = 55% closer, was 0.35)
            zoomSmoothness: 0.15,             // How smooth the zoom transition should be
            normalFOV: 75,                    // Normal field of view
            aimFOV: 45,                       // Field of view when aiming (more zoomed in)
            fovSmoothness: 0.15,               // How smooth the FOV transition should be
            aimTerrainBufferIncrease: 1.0,     // Extra buffer distance when aiming
            cameraCollisionOffset: 0.1          // Small offset for camera collision
        };

        // Override with any provided options
        Object.assign(this.options, options);

        // Set initial pitch to look slightly downward
        this.rotation = {
            yaw: 0,
            pitch: -0.2                       // Initial downward tilt
        };

        this.targetPosition = new THREE.Vector3();
        this.currentPosition = new THREE.Vector3();
        this.smoothedTargetY = 0;
        this.verticalVelocity = 0;
        this.forward = new THREE.Vector3();
        this.enabled = true;
        this.isAiming = false;               // Track aiming state
        this.currentDistanceMultiplier = 1;  // Current zoom level
        this.currentFOV = this.options.normalFOV;  // Current FOV
        
        // Enhanced vertical smoothing
        this.lastTargetY = 0;
        this.verticalVelocityHistory = [0, 0, 0, 0]; // Store last 4 velocities
        this.velocityIndex = 0;
        this.isJumping = false;
        this.jumpStartTime = 0;
        this.jumpDuration = 300; // ms
        this.previousHeights = [0, 0, 0]; // Track last 3 heights
        
        // Make the camera adjustable globally for debugging
        window.debugCamera = this.debugAdjustCamera.bind(this);
    }

    handleMouseMovement(movementX, movementY) {
        this.rotation.yaw += movementX * this.options.sensitivity;
        
        // Standard controls: moving mouse up looks up, moving mouse down looks down
        this.rotation.pitch = Math.max(
            this.options.minPitch,
            Math.min(this.options.maxPitch,
                this.rotation.pitch - movementY * this.options.sensitivity)
        );

        // Update target rotation to match camera direction for player model
        // But only use horizontal rotation (yaw)
        this.target.rotation.y = this.rotation.yaw;
    }

    getForwardDirection() {
        // Get camera's forward direction (for movement/shooting)
        this.forward.set(
            Math.sin(this.rotation.yaw) * Math.cos(this.rotation.pitch),
            -Math.sin(this.rotation.pitch),
            Math.cos(this.rotation.yaw) * Math.cos(this.rotation.pitch)
        );
        return this.forward.normalize();
    }

    // Get exact shooting direction for projectiles - no flattening
    getShootingDirection() {
        const direction = new THREE.Vector3();
        
        // Get direction from camera's rotation
        direction.set(
            Math.sin(this.rotation.yaw) * Math.cos(this.rotation.pitch),
            -Math.sin(this.rotation.pitch), // Negative sign needed for consistency with camera rotation
            Math.cos(this.rotation.yaw) * Math.cos(this.rotation.pitch)
        );
        
        return direction.normalize();
    }

    // Add new enable/disable methods
    enable() {
        this.enabled = true;
        document.body.classList.remove('editor-mode');
    }

    disable() {
        this.enabled = false;
    }

    // Add method to set aiming state
    setAiming(isAiming) {
        this.isAiming = isAiming;
    }

    // Method to receive jump state notifications from PlayerController
    setJumpState(isJumping) {
        this.isJumping = isJumping;
        if (isJumping) {
            this.jumpStartTime = performance.now();
        }
    }

    update(terrainManager) {
        // Get target Y position (player position + height offset)
        const targetY = this.target.position.y + this.options.height;
        
        // Detect potential jumps or rapid height changes
        const heightChange = targetY - this.lastTargetY;
        this.lastTargetY = targetY;
        
        // Update previous heights history
        this.previousHeights.push(targetY);
        this.previousHeights.shift();
        
        // Check if player appears to be jumping (sudden upward movement)
        if (heightChange > 0.2 && !this.isJumping) {
            this.isJumping = true;
            this.jumpStartTime = performance.now();
        }
        
        // Apply spring-damper smoothing to vertical movement with jump handling
        if (this.isJumping) {
            const elapsed = performance.now() - this.jumpStartTime;
            if (elapsed > this.jumpDuration) {
                this.isJumping = false;
            } else {
                // Enhanced smoothing during jumps
                const jumpProgress = elapsed / this.jumpDuration;
                // Ease in at start of jump, more responsive at end
                const smoothingFactor = 0.02 + (jumpProgress * 0.08);
                
                const deltaY = targetY - this.smoothedTargetY;
                this.verticalVelocity += deltaY * smoothingFactor;
                this.verticalVelocity *= 0.85; // Stronger damping during jumps
                this.smoothedTargetY += this.verticalVelocity;
            }
        } else {
            // Normal vertical smoothing for non-jump movement
            const deltaY = targetY - this.smoothedTargetY;
            
            // Add current velocity to history for averaging
            this.verticalVelocityHistory[this.velocityIndex] = deltaY * this.options.verticalSmoothingFactor;
            this.velocityIndex = (this.velocityIndex + 1) % this.verticalVelocityHistory.length;
            
            // Calculate average velocity from history (reduces oscillation)
            let avgVelocity = 0;
            for (let i = 0; i < this.verticalVelocityHistory.length; i++) {
                avgVelocity += this.verticalVelocityHistory[i];
            }
            avgVelocity /= this.verticalVelocityHistory.length;
            
            // Apply smoothed velocity with gradual acceleration curve
            this.verticalVelocity += avgVelocity;
            
            // Apply variable damping - stronger for bigger velocities
            const dampingFactor = 0.85 - Math.min(0.2, Math.abs(this.verticalVelocity) * 0.1);
            this.verticalVelocity *= dampingFactor;
            
            this.smoothedTargetY += this.verticalVelocity;
        }

        // Get normalized pitch factor (-1 to 1)
        const pitchFactor = this.rotation.pitch / (Math.PI/2);
        const pitchAbs = Math.abs(pitchFactor);
        
        // Calculate aim zoom
        const targetDistanceMultiplier = this.isAiming ? this.options.aimZoomMultiplier : 1.0;
        this.currentDistanceMultiplier = THREE.MathUtils.lerp(
            this.currentDistanceMultiplier,
            targetDistanceMultiplier,
            this.options.zoomSmoothness
        );

        // Separate handling for looking up vs down
        let distanceMultiplier = this.currentDistanceMultiplier;
        let heightAdjustment = 0;
        let terrainBufferMultiplier = 1.0;

        if (this.rotation.pitch > 0) { // Looking up
            // When aiming, reduce the distance increase slightly less to prevent terrain clipping
            const upwardDistanceIncrease = this.isAiming ? 0.7 : 1.5;
            distanceMultiplier *= 1.0 + (pitchAbs * upwardDistanceIncrease);
            heightAdjustment = -Math.abs(this.rotation.pitch) * 2.0;
            terrainBufferMultiplier = Math.max(0.5, 1.0 - pitchAbs);
        } else { // Looking down
            distanceMultiplier *= 1.0 - Math.abs(pitchFactor) * 0.4;
            heightAdjustment = Math.abs(this.rotation.pitch) * 6.0;
            terrainBufferMultiplier = 1.0 + pitchAbs;
        }

        // Calculate horizontal distance with dynamic adjustment based on pitch
        const horizontalDistance = this.options.distance * Math.cos(this.rotation.pitch) * distanceMultiplier;
        
        // Calculate camera position with adjustments
        this.targetPosition.set(
            this.target.position.x - Math.sin(this.rotation.yaw) * horizontalDistance + Math.cos(this.rotation.yaw) * this.options.horizontalOffset,
            this.smoothedTargetY + this.options.distance * Math.sin(this.rotation.pitch) + heightAdjustment,
            this.target.position.z + Math.cos(this.rotation.yaw) * horizontalDistance + Math.sin(this.rotation.yaw) * this.options.horizontalOffset
        );

        // Enhanced terrain collision check with variable buffer
        if (terrainManager) {
            // Increase buffer when aiming to prevent clipping with tighter zoom
            const aimBuffer = this.isAiming ? this.options.aimTerrainBufferIncrease : 0;
            const dynamicBuffer = (this.options.terrainBuffer + aimBuffer) * terrainBufferMultiplier;
            
            // Check terrain height at camera position
            const terrainHeight = terrainManager.getHeightAt(
                this.targetPosition.x,
                this.targetPosition.z
            ) + dynamicBuffer;
            
            // Only apply terrain height constraint when actually needed
            if (this.targetPosition.y < terrainHeight) {
                this.targetPosition.y = terrainHeight;
            }
            
            // Check terrain between camera and player
            const checkPoints = 5;
            let shouldRaiseCamera = false;
            let maxNeededHeight = this.targetPosition.y;
            
            // Only do detailed terrain checks when not looking steeply upward
            if (this.rotation.pitch < 0.3) { // About 17 degrees up
                for (let i = 1; i <= checkPoints; i++) {
                    const fraction = i / (checkPoints + 1);
                    const adjustedFraction = Math.pow(fraction, 0.7);
                    const checkX = this.target.position.x * adjustedFraction + this.targetPosition.x * (1 - adjustedFraction);
                    const checkZ = this.target.position.z * adjustedFraction + this.targetPosition.z * (1 - adjustedFraction);
                    
                    const pathHeight = terrainManager.getHeightAt(checkX, checkZ) + dynamicBuffer;
                    
                    if (pathHeight > maxNeededHeight) {
                        maxNeededHeight = pathHeight;
                        shouldRaiseCamera = true;
                    }
                }
                
                if (shouldRaiseCamera) {
                    this.targetPosition.y = maxNeededHeight;
                }
            }
        }

        // Smooth camera movement with separate horizontal and vertical smoothing
        this.camera.position.x = THREE.MathUtils.lerp(
            this.camera.position.x, 
            this.targetPosition.x, 
            this.options.smoothing
        );
        
        // Apply vertical smoothing based on jump state
        const verticalSmoothingFactor = this.isJumping ? 
            Math.max(0.05, this.options.smoothing * 0.5) : // Slower during jumps
            this.options.smoothing;
        
        this.camera.position.y = THREE.MathUtils.lerp(
            this.camera.position.y,
            this.targetPosition.y,
            verticalSmoothingFactor
        );
        
        this.camera.position.z = THREE.MathUtils.lerp(
            this.camera.position.z, 
            this.targetPosition.z, 
            this.options.smoothing
        );

        // Look target calculation
        const lookTarget = this.target.position.clone();
        lookTarget.x += Math.sin(this.rotation.yaw) * this.options.lookAheadDistance;
        lookTarget.z -= Math.cos(this.rotation.yaw) * this.options.lookAheadDistance;
        
        // Calculate vertical offset directly from pitch angle
        const heightOffset = this.options.lookAheadDistance * Math.tan(this.rotation.pitch);
        lookTarget.y = this.smoothedTargetY + heightOffset;
        
        this.camera.lookAt(lookTarget);
    }

    // Debug method to adjust camera parameters at runtime
    debugAdjustCamera(params) {
        console.log("Current camera settings:", { ...this.options });
        
        if (!params) {
            console.log("Usage: debugCamera({ horizontalOffset: 3.0, lookAheadDistance: 7.0 })");
            return;
        }
        
        // Update any provided parameters
        Object.keys(params).forEach(key => {
            if (this.options[key] !== undefined) {
                this.options[key] = params[key];
                console.log(`Updated ${key} to ${params[key]}`);
            } else {
                console.warn(`Unknown camera parameter: ${key}`);
            }
        });
        
        console.log("New camera settings:", { ...this.options });
        return this.options;
    }
    
    /**
     * Handle mobile touch input for camera rotation
     * @param {number} movementX - X movement from touch
     * @param {number} movementY - Y movement from touch
     */
    processMobileTouch(movementX, movementY) {
        // Use a different sensitivity for touch to make it feel natural
        const touchSensitivity = this.options.sensitivity * 0.5;
        
        // Process inputs similar to mouse but with touch sensitivity
        this.rotation.yaw += movementX * touchSensitivity;
        
        // Prevent camera from flipping over
        this.rotation.pitch = Math.max(
            this.options.minPitch,
            Math.min(this.options.maxPitch,
                this.rotation.pitch - movementY * touchSensitivity)
        );
        
        // Update target rotation to match camera direction for player model
        if (this.target) {
            this.target.rotation.y = this.rotation.yaw;
        }
    }
    
    /**
     * Alternative method name for mobile rotation (can be called from PlayerController)
     * @param {number} deltaX - X movement
     * @param {number} deltaY - Y movement
     */
    onMobileRotation(deltaX, deltaY) {
        this.processMobileTouch(deltaX, deltaY);
    }
}
